/**
 * TS-334 — mobile PDP gallery wrap-around.
 *
 * Desktop loops last→first because the arrow buttons go through
 * `Slideshow.select()`, which wraps out-of-range indexes when the component
 * has the `infinite` attribute. On mobile the arrows are hidden and swiping
 * is native CSS scroll-snap, which physically cannot scroll past the last
 * slide — so no code ever runs to wrap. This module bridges that gap: when a
 * deliberate forward swipe ends while the gallery is already pinned on its
 * last slide, it calls `slideshow.next()` and lets the existing infinite
 * logic (placeholder + reorder trick) animate the wrap. The mirror gesture
 * on the first slide wraps backward, matching the desktop previous arrow.
 *
 * Listeners are delegated on the document so the enhancement survives
 * section re-renders and variant-change DOM morphs without lifecycle
 * management. Zoom and quick-view galleries live inside a <dialog> and are
 * deliberately excluded.
 */

/** Minimum horizontal travel (px) for a swipe to count as deliberate. */
const MIN_SWIPE_DISTANCE = 40;

/** @typedef {HTMLElement & { atStart: boolean, atEnd: boolean, infinite: boolean, next: (event?: Event) => void, previous: (event?: Event) => void }} SlideshowElement */

/**
 * @typedef {Object} Gesture
 * @property {SlideshowElement} slideshow - The gallery slideshow being swiped.
 * @property {Element} scroller - The slideshow's media scroller.
 * @property {number} startX - Touch clientX at gesture start.
 * @property {number} startY - Touch clientY at gesture start.
 * @property {boolean} wasAtEnd - Whether the slideshow was on its last slide at gesture start.
 * @property {boolean} wasAtStart - Whether the slideshow was on its first slide at gesture start.
 */

/** @type {Gesture | null} */
let gesture = null;

/**
 * Finds the main PDP gallery slideshow for a touch that started on its media
 * scroller, if any. Gestures on the controls (dots, thumbnail strip, arrows)
 * are ignored — swiping a scrollable thumbnail strip must not page the
 * gallery.
 * @param {EventTarget | null} target
 * @returns {{ slideshow: SlideshowElement, scroller: Element } | null}
 */
const findGallerySlideshow = (target) => {
  if (!(target instanceof Element)) return null;

  // Interacting with a 3D model must never page the gallery
  if (target.closest('model-viewer')) return null;

  // Only gestures on the media scroller count, not the surrounding controls
  const scroller = target.closest('slideshow-slides');
  if (!scroller) return null;

  const slideshow = scroller.closest('media-gallery slideshow-component');
  if (!slideshow) return null;

  // Zoom / quick-view galleries render inside a <dialog> — leave them alone
  if (slideshow.closest('dialog')) return null;

  const candidate = /** @type {SlideshowElement} */ (slideshow);
  if (typeof candidate.next !== 'function' || typeof candidate.previous !== 'function') return null;

  return { slideshow: candidate, scroller };
};

/** @param {TouchEvent} event */
const handleTouchStart = (event) => {
  // A second finger means pinch/zoom, not a swipe
  if (event.touches.length > 1) {
    gesture = null;
    return;
  }

  const found = findGallerySlideshow(event.target);
  if (!found || !found.slideshow.infinite) return;

  const { atEnd, atStart } = found.slideshow;
  if (!atEnd && !atStart) return;

  const touch = event.touches[0];
  if (!touch) return;

  gesture = {
    slideshow: found.slideshow,
    scroller: found.scroller,
    startX: touch.clientX,
    startY: touch.clientY,
    wasAtEnd: atEnd,
    wasAtStart: atStart,
  };
};

/** @param {TouchEvent} event */
const handleTouchEnd = (event) => {
  const current = gesture;
  gesture = null;

  if (!current) return;
  // Fingers still down — part of a multi-touch interaction
  if (event.touches.length > 0) return;

  const touch = event.changedTouches[0];
  if (!touch) return;

  const deltaX = touch.clientX - current.startX;
  const deltaY = touch.clientY - current.startY;

  // Must be a deliberate, predominantly horizontal swipe
  if (Math.abs(deltaX) < MIN_SWIPE_DISTANCE || Math.abs(deltaX) <= Math.abs(deltaY)) return;

  const { slideshow, scroller } = current;
  const rtl = getComputedStyle(scroller).direction === 'rtl';
  // Finger travel toward "next": leftward in LTR, rightward in RTL
  const isForward = rtl ? deltaX > 0 : deltaX < 0;

  // Re-check position: momentum from an earlier gesture may have moved us
  if (isForward && current.wasAtEnd && slideshow.atEnd) {
    slideshow.next();
  } else if (!isForward && current.wasAtStart && slideshow.atStart) {
    slideshow.previous();
  }
};

const handleTouchCancel = () => {
  gesture = null;
};

document.addEventListener('touchstart', handleTouchStart, { passive: true });
document.addEventListener('touchend', handleTouchEnd, { passive: true });
document.addEventListener('touchcancel', handleTouchCancel, { passive: true });
